'use client';

import { cn } from '@lib/util/cn';
import LocalizedClientLink from '@modules/common/components/localized-client-link';
import { usePathname } from 'next/navigation';

const STEPS = [
  { number: 1, label: 'BASKET' },
  { number: 2, label: 'PAYMENT' },
  { number: 3, label: 'ORDER COMPLETE' },
] as const;

export default function CheckoutStepsBar() {
  const pathname = usePathname();
  const activeStep = getActiveStep(pathname);

  return (
    <ol
      aria-label="Checkout progress"
      className="bg-brand-interactive-800 flex items-center justify-center gap-4 py-2 desktop:py-4 w-full list-none"
    >
      {STEPS.map((step) => {
        const isActive = step.number <= activeStep;
        const isCurrent = step.number === activeStep;

        return (
          <li
            key={step.number}
            className="flex items-center gap-1"
            aria-current={isCurrent ? 'step' : undefined}
          >
            {step.number === 1 && activeStep !== 1 ? (
              <LocalizedClientLink
                href="/basket"
                className="flex items-center gap-1"
                aria-label="Go to basket, step 1"
              >
                <StepIndicator number={step.number} isActive={isActive} />
                <span className="text-white text-[10px] desktop:text-xs font-normal whitespace-nowrap">
                  {step.label}
                </span>
              </LocalizedClientLink>
            ) : (
              <>
                <StepIndicator number={step.number} isActive={isActive} />
                <span className="text-white text-[10px] desktop:text-xs font-normal whitespace-nowrap">
                  {step.label}
                </span>
              </>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function StepIndicator({
  number,
  isActive,
}: {
  number: number;
  isActive: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full shrink-0 size-5 desktop:size-[25px]',
        isActive
          ? 'bg-white text-brand-interactive-800'
          : 'border border-white text-white',
      )}
      aria-hidden="true"
    >
      <span className="text-[10px] desktop:text-xs font-normal leading-none">
        {number}
      </span>
    </div>
  );
}

function getActiveStep(pathname: string): number {
  if (pathname.startsWith('/order/') && pathname.endsWith('/confirmed')) {
    return 3;
  }
  if (pathname.startsWith('/checkout')) {
    return 2;
  }
  return 1;
}
